import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, MessageCircle, Globe2, Calendar, Image as ImageIcon, BarChart3 } from "lucide-react";
import MarketingLayout, { useMLang } from "./MarketingLayout";

const HOME = {
    en: {
        badge: "Restaurant SaaS · Now in beta",
        h1a: "A beautiful restaurant site in ",
        h1highlight1: "five minutes",
        h1b: ". Orders straight to ",
        h1highlight2: "WhatsApp",
        h1c: ".",
        sub: "HeavenlyEats gives Latin restaurants a fully bilingual, fully branded online ordering site — no card processing required. Your customers tap a dish, you get a WhatsApp message, you cook, you get paid. That simple.",
        ctaDemo: "Try the live demo",
        ctaPricing: "See pricing",
        featuresTitle: "Built for the way Latin restaurants actually sell",
        features: [
            { i: MessageCircle, t: "WhatsApp checkout", b: "Customers tap, you get a perfectly-formatted order in WhatsApp. No payment gateway required." },
            { i: Globe2, t: "Bilingual (EN / ES)", b: "Every label, every email, every receipt — translated for the diaspora." },
            { i: Calendar, t: "Catering + calendar", b: "Plan multi-day prep, broadcast pickup reminders to whole days at once." },
            { i: ImageIcon, t: "AI menu photography", b: "Generate appetizing dish photos in seconds with Gemini Nano Banana." },
            { i: BarChart3, t: "Profitability dashboard", b: "See cost-per-dish, suggested prices, top sellers and 7-day revenue." },
            { i: Sparkles, t: "100% white-label", b: "Your brand, your colors, your country flag — even the chef bio. Zero HeavenlyEats branding on your site." },
        ],
        socialProofTitle: "Used by Dominican kitchens in Jamaica",
        socialProofBody: "Our flagship customer, Delicias Dominicanas, takes WhatsApp orders for fresh sancocho, mangú and pasteles every day. Click below to see exactly what your site will look like.",
        socialProofCta: "Open Delicias Dominicanas →",
    },
    es: {
        badge: "SaaS para restaurantes · En beta",
        h1a: "Un sitio para tu restaurante en ",
        h1highlight1: "cinco minutos",
        h1b: ". Pedidos directos a ",
        h1highlight2: "WhatsApp",
        h1c: ".",
        sub: "HeavenlyEats le da a los restaurantes latinos un sitio bilingüe, totalmente marca propia y listo para pedidos en línea — sin procesar tarjetas. Tu cliente toca un plato, tú recibes el WhatsApp, cocinas, cobras. Así de simple.",
        ctaDemo: "Prueba el demo",
        ctaPricing: "Ver precios",
        featuresTitle: "Hecho para la forma en que los restaurantes latinos realmente venden",
        features: [
            { i: MessageCircle, t: "Pago por WhatsApp", b: "El cliente toca, tú recibes un pedido perfectamente formateado en WhatsApp. Sin pasarela de pagos." },
            { i: Globe2, t: "Bilingüe (EN / ES)", b: "Cada etiqueta, cada correo, cada recibo — traducido para la diáspora." },
            { i: Calendar, t: "Catering + calendario", b: "Planifica varios días de prep y envía recordatorios a clientes por día completo." },
            { i: ImageIcon, t: "Fotos de menú con IA", b: "Genera fotos apetitosas de tus platos en segundos con Gemini Nano Banana." },
            { i: BarChart3, t: "Panel de rentabilidad", b: "Costo por plato, precio sugerido, los más vendidos e ingreso de 7 días." },
            { i: Sparkles, t: "100% marca blanca", b: "Tu marca, tus colores, tu bandera — hasta la bio del chef. Cero branding de HeavenlyEats en tu sitio." },
        ],
        socialProofTitle: "Usado por cocinas dominicanas en Jamaica",
        socialProofBody: "Nuestra cliente insignia, Delicias Dominicanas, recibe pedidos por WhatsApp de sancocho, mangú y pasteles todos los días. Toca abajo para ver exactamente cómo lucirá tu sitio.",
        socialProofCta: "Abrir Delicias Dominicanas →",
    },
};

const MarketingHome = () => {
    const { lang } = useMLang();
    const c = HOME[lang];
    return (
        <MarketingLayout testid="marketing-home">
            {/* Hero */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.2em] font-bold border border-white/15 bg-white/5 text-white/80">
                        <Sparkles className="w-3 h-3" /> {c.badge}
                    </span>
                    <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mt-6 tracking-tight">
                        {c.h1a}<span className="text-orange-400">{c.h1highlight1}</span>{c.h1b}<span className="text-green-400">{c.h1highlight2}</span>{c.h1c}
                    </h1>
                    <p className="text-lg sm:text-xl text-white/70 mt-7 leading-relaxed max-w-2xl">{c.sub}</p>
                    <div className="mt-9 flex flex-wrap gap-4">
                        <Link to="/r/delicias" data-testid="home-cta-demo" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-semibold shadow-xl shadow-orange-900/30 transition-all hover:scale-[1.02]">
                            {c.ctaDemo}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link to="/heavenly/pricing" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold transition-all">
                            {c.ctaPricing}
                        </Link>
                    </div>
                </motion.div>
            </section>

            {/* Feature grid */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-20">
                <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-10 max-w-2xl">{c.featuresTitle}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {c.features.map((f, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                            className="p-6 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur"
                            data-testid={`home-feature-${i}`}
                        >
                            <div className="w-10 h-10 rounded-xl bg-orange-500/15 text-orange-300 flex items-center justify-center mb-4">
                                <f.i className="w-5 h-5" />
                            </div>
                            <h3 className="font-serif font-bold text-xl mb-2">{f.t}</h3>
                            <p className="text-sm text-white/60 leading-relaxed">{f.b}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Social proof */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
                <div className="rounded-3xl border border-orange-400/30 bg-orange-500/[0.06] p-8 sm:p-12">
                    <h2 className="font-serif font-bold text-3xl sm:text-4xl mb-4 max-w-xl">{c.socialProofTitle}</h2>
                    <p className="text-white/70 max-w-2xl mb-6">{c.socialProofBody}</p>
                    <Link to="/r/delicias" data-testid="home-social-cta" className="inline-flex items-center gap-2 text-orange-300 hover:text-orange-200 font-semibold">
                        {c.socialProofCta}
                    </Link>
                </div>
            </section>
        </MarketingLayout>
    );
};

export default MarketingHome;
